import XIcon from '@duyank/icons/regular/X';
import { useEditor } from '@lidojs/design-editor';
import { fetchSvgContent } from '@lidojs/design-utils';
import React, { ChangeEvent, FC, useContext, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import IconButton from '../../IconButton';
import { RiCloseLine, RiUpload2Line } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useDropArea } from 'react-use';
import { EditorContext } from '@lidojs/design-editor';
import { useTheme } from '@emotion/react';

// import { RiUpload2Line } from '@remixicon/react';

interface UploadContentProps {
  visibility: boolean;
  onClose: () => void;
  images: { url: string; type: 'svg' | 'image' }[];
  onAddImage: (images: { url: string; type: 'svg' | 'image' }) => void;
}

const UploadContent: FC<UploadContentProps> = ({
  visibility,
  onClose,
  images,
  onAddImage,
}: UploadContentProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { actions } = useEditor();
  const { t } = useTranslation();
  const { uploadImage } = useContext(EditorContext);
  const currentTheme = useTheme();

  const addImage = async (url: string) => {
    const img = new Image();
    img.onerror = (err) => window.alert(err);
    img.src = url;
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      actions.addImageLayer(
        { url, thumb: url },
        { width: img.naturalWidth, height: img.naturalHeight }
      );
      if (isMobile) {
        onClose();
      }
    };
  };
  const addSvg = async (url: string) => {
    const ele = await fetchSvgContent(url);
    const viewBox = ele.getAttribute('viewBox')?.split(' ') || [];
    const width =
      viewBox.length === 4 ? +viewBox[2] : +(ele.getAttribute('width') || 100);
    const height =
      viewBox.length === 4 ? +viewBox[3] : +(ele.getAttribute('height') || 100);
    actions.addSvgLayer(url, { width, height }, ele);
    if (isMobile) {
      onClose();
    }
  };

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const { url } = await uploadImage(file);
        onAddImage({
          url: url as string,
          type: file.type === 'image/svg+xml' ? 'svg' : 'image',
        });
      } else {
        console.error('Invalid file type. Only image files are allowed.');
      }
    }
  };

  const [bond, state] = useDropArea({
    onFiles: (files) => {
      const imageFiles = Array.from(files).filter((file) =>
        file.type.startsWith('image/')
      );
      if (imageFiles.length > 0) {
        const readerPromises = imageFiles.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                url: reader.result as string,
                type: file.type === 'image/svg+xml' ? 'svg' : 'image',
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        Promise.all(readerPromises)
          .then((results) => {
            results.forEach((item: { url: string; type: 'svg' | 'image' }) => {
              onAddImage(item);
            });
          })
          .catch((error) => {
            console.error('Error reading image files:', error);
          });
      }
    },
  });

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: visibility ? 'flex' : 'none',
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 48,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          {t('page_editor.general.upload')}
        </p>
        <IconButton onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </div>

      {/* dropzone */}
      <input
        ref={inputFileRef}
        accept="image/*"
        css={(theme) => ({ display: 'none' })}
        type={'file'}
        onChange={handleUpload}
      />

      <div
        {...bond}
        css={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 12,
          border: '1px dashed',
          borderColor: theme.name === 'dark' ? '#3A3A3C' : '#D0D5DD',
          backgroundColor: state.over
            ? theme.name === 'dark'
              ? '#2C2C2E'
              : '#EDF8F8'
            : theme.name === 'dark'
            ? '#1C1C1E'
            : '#FFFFFF',
          cursor: 'pointer',
          transition: 'background-color 0.3s',
          padding: '40px 16px',
          margin: '16px 20px',
          ':hover': {
            backgroundColor: theme.name === 'dark' ? '#2C2C2E' : '#EDF8F8',
          },
        })}
        onClick={() => inputFileRef.current?.click()}
      >
        <div
          css={(theme) => ({
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: theme.name === 'dark' ? '#333333' : '#E7F0F0',
            border: '8px solid',
            borderColor:
              theme.name === 'dark'
                ? '#444444'
                : state.over
                ? '#EAF1F1'
                : '#F0F7F7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <RiUpload2Line
            size={24}
            color={currentTheme.name === 'dark' ? '#FFFFFF' : '#475467'}
          />
        </div>
        <div
          css={(theme) => ({
            marginTop: 16,
            fontSize: 14,
            fontWeight: 'bold',
          })}
        >
          {t('page_editor.upload.upload_image')}
        </div>
        <div
          css={(theme) => ({
            marginTop: 8,
            fontSize: 12,
          })}
        >
          <span
            css={(theme) => ({
              color: '#667085',
            })}
          >
            {t('page_editor.upload.type')}
          </span>{' '}
          {t('page_editor.upload.type_image')}
        </div>

        <div
          css={(theme) => ({
            marginTop: 4,
            fontSize: 12,
          })}
        >
          <span
            css={(theme) => ({
              color: '#667085',
            })}
          >
            {t('page_editor.upload.size')}{' '}
          </span>
          {t('page_editor.upload.maximum_size')}
        </div>
      </div>
      {/* end of dropzone */}

      {/* <div css={{
          margin: 16,
          marginBottom: 8,
          background:  theme.colors.background,
          borderRadius: 8,
          border: '1px solid #d9d9d9',
          padding: '8px 16px',
          cursor: 'pointer',
          textAlign: 'center',
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          transition: 'all 0.2s',
          ':hover': {
            background: '#E8E8E8',
          },
        }}
        onClick={() => inputFileRef.current?.click()}
      >
        <RiUpload2Line size={14} />
        <span>{t('page_editor.upload.upload_image')}</span>
      </div> */}

      <div
        css={(theme) => ({
          padding: '16px',
        })}
      >
        <div
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
            gridGap: 8,
            paddingBottom: '10px',
          })}
        >
          {images.map((item, idx) => (
            <div
              key={idx}
              css={(theme) => ({
                cursor: 'pointer',
                position: 'relative',
                width: '100%',
                paddingBottom: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '16px',
                aspectRatio: '1/1',
                borderRadius: 5,
                transition: 'box-shadow 0.3s',
                ':hover': {
                  boxShadow: theme.shadow.hoverItem,
                },
              })}
              onClick={() =>
                item.type === 'image' ? addImage(item.url) : addSvg(item.url)
              }
            >
              <div
                css={(theme) => ({
                  paddingBottom: '100%',
                  height: 0,
                })}
              />
              <div
                css={(theme) => ({
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                })}
              >
                <img
                  css={(theme) => ({ maxHeight: '100%' })}
                  loading="lazy"
                  src={item.url}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UploadContent;
